import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import DynamicTable from '../../components/Table/Table';
import { fetchMaterialMasterData } from './services';

const MaterialSkuMasterTable = () => {
  const [data, setData] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const selectedBu = useSelector((state) => state?.cycle?.selectedBu);
  const columns = [
    { id: 'MaterialID', label: 'MATERIAL ID', type: 'text', mappingCategory: 'attributes', defaultValue: '-' },
    { id: 'MaterialName', label: 'MATERIAL NAME', type: 'text', defaultValue: '-' },
    {
      id: 'MaterialGlobalTradeItemNumber',
      label: 'UPC/GTIN',
      type: 'text',
      mappingCategory: 'attributes',
      defaultValue: '-'
    },
    { id: 'TPOSKU', label: 'SKU ID', type: 'text', defaultValue: '-' },
    { id: 'TPOSKUName', label: 'SKU NAME', type: 'text', defaultValue: '-' },
    {
      id: 'TPOSkuClassName',
      label: 'MANUFACTURING CLASS',
      type: 'text',
      mappingCategory: 'attributes',
      defaultValue: '-'
    },
    { id: 'ProductHierarchyLevel1Description', label: 'PRODUCT LINE', type: 'text', defaultValue: '-' },
    { id: 'ProductHierarchyLevel2Description', label: 'PRODUCT GROUP', type: 'text', defaultValue: '-' },
    { id: 'BrandName', label: 'BRAND', type: 'text', mappingCategory: 'attributes', defaultValue: '-' },
    { id: 'SubBrandName', label: 'SUB-BRAND', type: 'text', mappingCategory: 'attributes', defaultValue: '-' },
    { id: 'TPOSkuFlavorName', label: 'FLAVOR', type: 'text', mappingCategory: 'attributes', defaultValue: '-' },
    { id: 'TPOSkuSizeName', label: 'SIZE', type: 'text', mappingCategory: 'attributes', defaultValue: '-' },
    { id: 'PackageTypeName', label: 'PACK TYPE', type: 'text', mappingCategory: 'finance', defaultValue: '-' },
    {
      id: 'MaterialNetWeightQuantity',
      label: 'WEIGHT',
      type: 'number',
      mappingCategory: 'finance',
      defaultValue: 0
    },
    { id: 'GrossSales', label: 'GROSS SALES', type: 'number', mappingCategory: 'finance', defaultValue: 0 },
    { id: 'GSvsLY', label: 'GS vs LY', type: 'number', mappingCategory: 'finance', defaultValue: 0 },
    {
      id: 'OnTicketTradeSpend',
      label: 'ON-TICKET TRADE SPEND',
      type: 'number',
      mappingCategory: 'finance',
      defaultValue: 0
    },
    {
      id: 'NetInvoiceSales',
      label: 'NET INVOICE SALES',
      type: 'number',
      mappingCategory: 'finance',
      defaultValue: 0
    },
    { id: 'NSvsLY', label: 'NS vs LY', type: 'number', mappingCategory: 'finance', defaultValue: 0 },
    {
      id: 'OffTicketTradeSpend',
      label: 'OFF-TICKET TRADE SPEND',
      type: 'number',
      mappingCategory: 'finance',
      defaultValue: 0
    },
    { id: 'NetRevenue', label: 'NET REVENUE', type: 'number', mappingCategory: 'finance', defaultValue: 0 },
    { id: 'COGS', label: 'COGS', type: 'number', mappingCategory: 'finance', defaultValue: 0 },
    { id: 'GrossProfit', label: 'GROSS PROFIT', type: 'number', mappingCategory: 'finance', defaultValue: 0 },
    { id: 'GPPercent', label: 'GP %', type: 'number', mappingCategory: 'finance', defaultValue: 0 },
    { id: 'TotalSD', label: 'TOTAL S&D', type: 'number', mappingCategory: 'finance', defaultValue: 0 },
    { id: 'NetProfit', label: 'NET PROFIT', type: 'number', mappingCategory: 'finance', defaultValue: 0 },
    { id: 'NPvsLY', label: 'NP vs LY', type: 'number', mappingCategory: 'finance', defaultValue: 0 },
    { id: 'CasesvsLY', label: 'CASES vs LY', type: 'number', mappingCategory: 'finance', defaultValue: 0 },
    { id: 'WeightvsLY', label: 'WEIGHT vs LY', type: 'number', mappingCategory: 'finance', defaultValue: 0 },
    { id: 'TotalWaste', label: 'TOTAL WASTE ($)', type: 'number', mappingCategory: 'finance', defaultValue: 0 },
    {
      id: 'FacilityCountL12W',
      label: 'FACILITY COUNT L12W',
      type: 'number',
      mappingCategory: 'finance',
      defaultValue: 0
    },
    { id: 'NetVolume', label: 'VOLUME QTY', type: 'number', defaultValue: 0 }
  ];
  
  const handleDownload = async () => {
    try {
      const content = await getMaterialMasterData();
      // List of columns with new names to include in the download
      const columnMapping = {
        MaterialID: 'MATERIAL ID',
        MaterialName: 'MATERIAL NAME',
        MaterialGlobalTradeItemNumber: 'UPC/GTIN',
        TPOSKU: 'SKU ID',
        TPOSKUName: 'SKU NAME',
        TPOSkuClassName: 'MANUFACTURING CLASS',
        ProductHierarchyLevel1Description: 'PRODUCT LINE',
        ProductHierarchyLevel2Description: 'PRODUCT GROUP',
        BrandName: 'BRAND',
        SubBrandName: 'SUB-BRAND',
        TPOSkuFlavorName: 'FLAVOR',
        TPOSkuSizeName: 'SIZE',
        PackageTypeName: 'PACK TYPE',
        MaterialNetWeightQuantity: 'WEIGHT',
        GrossSales: 'GROSS SALES',
        GSvsLY: 'GS vs LY',
        OnTicketTradeSpend: 'ON-TICKET TRADE SPEND',
        NetInvoiceSales: 'NET INVOICE SALES',
        NSvsLY: 'NS vs LY',
        OffTicketTradeSpend: 'OFF-TICKET TRADE SPEND',
        NetRevenue: 'NET REVENUE',
        COGS: 'COGS',
        GrossProfit: 'GROSS PROFIT',
        GPPercent: 'GP %',
        TotalSD: 'TOTAL S&D',
        NetProfit: 'NET PROFIT',
        NPvsLY: 'NP vs LY',
        CasesvsLY: 'CASES vs LY',
        WeightvsLY: 'WEIGHT vs LY',
        TotalWaste: 'TOTAL WASTE ($)',
        FacilityCountL12W: 'FACILITY COUNT L12W',
        NetVolume: 'VOLUME QTY'
      };

      // Transform content to only include specified columns and rename them
      const transformedContent = content.map((row) => {
        const transformedRow = {};
        Object.keys(columnMapping).forEach((key) => {
          if (key) {
            transformedRow[columnMapping[key]] = row[key] !== undefined ? row[key] : '';
          }
        });
        return transformedRow;
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(transformedContent);

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, `${selectedBu}_MaterialMaster.xlsx`);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const getMaterialMasterData = async () => {
    setIsTableLoading(true);
    const allData = [];
    let pageNumber = 0;
    const dataLimitPerPage = 2000;
    let shouldContinue = true;
    try {
      while (shouldContinue) {
        // Added this condition as we are refreshing the page , redux state is getting empty
        // Quick fix around
        const { data2 } = await fetchMaterialMasterData(selectedBu ? selectedBu : 'NA', pageNumber);
        allData.push(...data2);
        if (data2?.length < dataLimitPerPage) {
          shouldContinue = false;
        } else {
          pageNumber++;
        }
      }
      setData(allData);
      return allData;
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsTableLoading(false);
    }
  };

  useEffect(() => {
    getMaterialMasterData();
  }, []);

  return (
    <DynamicTable
      originalColumns={columns}
      originalData={data}
      tableName="SKU Master"
      isTableLoading={isTableLoading}
      isColumnVisibleFilter
      isColumnFilter
      downloadExcel
      handleDownloadData={handleDownload}
      isCollapseButton={false}
      density="small"
      isSearch
    />
  );
};

export default MaterialSkuMasterTable;
